/**
 * Fechas, tamaños e importes cortos de las listas de Clientes (2.5 a 2.8), como los pinta el diseño: «9 oct», «Hoy, 10:22»,
 * «Ayer», «2 oct, 18:05», «el 9 nov», «48 KB», «412 €». Hora local del navegador (la del negocio en el prototipo).
 */
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DIA_MS = 86_400_000;

function fecha(iso: string | null | undefined): Date | null {
    if (iso == null || iso === '') {
        return null;
    }
    const f = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
    return Number.isNaN(f.getTime()) ? null : f;
}

function mismoDia(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const dosCifras = (n: number) => String(n).padStart(2, '0');

/** «9 oct» (con el año si no es el de `ahora`: «12 mar 2025»). */
export function diaMes(iso: string | null | undefined, ahora: Date = new Date()): string {
    const f = fecha(iso);
    if (f === null) {
        return '';
    }
    const base = `${String(f.getDate())} ${MESES[f.getMonth()] ?? ''}`;
    return f.getFullYear() === ahora.getFullYear() ? base : `${base} ${String(f.getFullYear())}`;
}

/** «Hoy, 10:22», «Ayer, 18:05» o «2 oct, 18:05». */
export function diaYHora(iso: string | null | undefined, ahora: Date = new Date()): string {
    const f = fecha(iso);
    if (f === null) {
        return '';
    }
    const hora = `${dosCifras(f.getHours())}:${dosCifras(f.getMinutes())}`;
    if (mismoDia(f, ahora)) {
        return `Hoy, ${hora}`;
    }
    if (mismoDia(f, new Date(ahora.getTime() - DIA_MS))) {
        return `Ayer, ${hora}`;
    }
    return `${diaMes(iso, ahora)}, ${hora}`;
}

/** «Hoy», «Ayer» o «9 oct»: cuándo se detectó algo. */
export function cuando(iso: string | null | undefined, ahora: Date = new Date()): string {
    const f = fecha(iso);
    if (f === null) {
        return '';
    }
    if (mismoDia(f, ahora)) {
        return 'Hoy';
    }
    if (mismoDia(f, new Date(ahora.getTime() - DIA_MS))) {
        return 'Ayer';
    }
    return diaMes(iso, ahora);
}

/** Días enteros que faltan hasta esa fecha (0 = hoy; negativo si ya pasó). */
export function diasHasta(iso: string | null | undefined, ahora: Date = new Date()): number {
    const f = fecha(iso);
    if (f === null) {
        return 0;
    }
    const inicio = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return Math.round((inicio(f) - inicio(ahora)) / DIA_MS);
}

/** Hoy en `AAAA-MM-DD` (fecha local), para el máximo de una fecha que solo puede ser pasada. */
export function hoyLocal(ahora: Date = new Date()): string {
    return `${String(ahora.getFullYear())}-${dosCifras(ahora.getMonth() + 1)}-${dosCifras(ahora.getDate())}`;
}

/** «48 KB» (como mínimo 1 KB). */
export function kilobytes(bytes: number | null | undefined): string {
    if (bytes == null) {
        return '';
    }
    return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toLocaleString('es-ES', { maximumFractionDigits: 1 })} MB` : `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

/** «412 €» o «86,50 €» (el importe llega en céntimos). */
export function importe(dinero: { amount: number; currency: string } | null | undefined): string {
    if (dinero == null) {
        return '0 €';
    }
    const euros = dinero.amount / 100;
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: dinero.currency, minimumFractionDigits: Number.isInteger(euros) ? 0 : 2 }).format(euros);
}

/** «1.240» (cifras con punto de miles). */
export function cifra(n: number): string {
    return n.toLocaleString('es-ES');
}

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** «sábado 18 oct, 21:30»: cuándo es un compromiso que se cancela (G151). */
export function diaConHora(iso: string | null | undefined): string {
    const f = fecha(iso);
    if (f === null) {
        return '';
    }
    return `${DIAS[f.getDay()] ?? ''} ${String(f.getDate())} ${MESES[f.getMonth()] ?? ''}, ${dosCifras(f.getHours())}:${dosCifras(f.getMinutes())}`;
}
